import React from 'react';
import { Activity, TrendingDown, AlertCircle, DollarSign, Package, Calendar, Target } from 'lucide-react';

const MetricsDashboard = ({ calculations, data, formatCurrency }) => {
  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const currentCash = parseNum(data?.currentCash);
  const criticalLevel = calculations?.criticalCashLevel;
  const healthScore = criticalLevel > 0 ? Math.min(100, (currentCash / criticalLevel) * 100) : 0;

  const getHealthColor = () => {
    if (healthScore >= 80) return 'green';
    if (healthScore >= 50) return 'blue';
    if (healthScore >= 30) return 'amber';
    return 'red';
  };

  const healthColor = getHealthColor();

  const alerts = [];
  
  if (calculations?.cashRunwayMonths < 3) {
    alerts?.push({
      type: 'critical',
      icon: AlertCircle,
      title: 'Critical Runway',
      message: 'Less than 3 months cash remaining'
    });
  }

  if (currentCash < criticalLevel) {
    alerts?.push({
      type: 'warning',
      icon: TrendingDown,
      title: 'Below Critical Level',
      message: 'Cash position below 3-month operating threshold'
    });
  }

  if (calculations?.reorderPoint > 0 && parseNum(data?.quantityNeeded) < calculations?.reorderPoint) {
    alerts?.push({
      type: 'info',
      icon: Package,
      title: 'Reorder Point Reached',
      message: 'Time to place new inventory order'
    });
  }

  return (
    <div className="sticky top-24 space-y-4">
      {/* Financial Health Score */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h3 className="text-sm font-bold text-slate-900 mb-4">Financial Health Score</h3>
        
        <div className="relative w-full h-3 bg-slate-200 rounded-full overflow-hidden mb-3">
          <div 
            className={`absolute top-0 left-0 h-full bg-${healthColor}-500 transition-all duration-500`}
            style={{ width: `${healthScore}%` }}
          ></div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className={`text-2xl font-bold text-${healthColor}-600`}>
            {healthScore?.toFixed(0)}%
          </span>
          <Activity className={`w-6 h-6 text-${healthColor}-600`} />
        </div>
        
        <p className="text-xs text-slate-600 mt-2">
          Based on cash position vs. critical level
        </p>
      </div>

      {/* Key Metrics */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h3 className="text-sm font-bold text-slate-900 mb-4">Key Metrics</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-slate-600">Cash Position</span>
            </div>
            <strong className="text-sm text-slate-900">
              {formatCurrency(currentCash)}
            </strong>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-orange-600" />
              <span className="text-xs text-slate-600">Monthly Burn</span>
            </div>
            <strong className="text-sm text-slate-900">
              {formatCurrency(calculations?.totalMonthlyBurn)}
            </strong>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-purple-600" />
              <span className="text-xs text-slate-600">Runway</span>
            </div>
            <strong className="text-sm text-slate-900">
              {calculations?.cashRunwayMonths?.toFixed(1)} mo
            </strong>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-green-600" />
              <span className="text-xs text-slate-600">Inventory Turnover</span>
            </div>
            <strong className="text-sm text-slate-900">
              {calculations?.monthlyInventoryTurnover?.toFixed(1)}x
            </strong>
          </div>

          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-900">Break Even</span>
            </div>
            <strong className="text-sm text-blue-700">
              {formatCurrency(calculations?.breakEvenPoint)}
            </strong>
          </div>
        </div>
      </div>

      {/* Real-time Alerts */}
      {alerts?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-sm font-bold text-slate-900 mb-4">Alerts & Notifications</h3>
          
          <div className="space-y-3">
            {alerts?.map((alert, index) => {
              const AlertIcon = alert?.icon;
              const alertColors = {
                critical: 'red',
                warning: 'amber',
                info: 'blue'
              };
              const color = alertColors?.[alert?.type] || 'slate';
              
              return (
                <div key={index} className={`p-3 bg-${color}-50 rounded-lg border border-${color}-200`}>
                  <div className="flex items-start gap-2">
                    <AlertIcon className={`w-4 h-4 text-${color}-600 flex-shrink-0 mt-0.5`} />
                    <div>
                      <p className={`text-xs font-medium text-${color}-900 mb-0.5`}>
                        {alert?.title}
                      </p>
                      <p className={`text-xs text-${color}-700`}>
                        {alert?.message}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Critical Levels Reference */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h3 className="text-sm font-bold text-slate-900 mb-4">Critical Levels</h3>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center pb-2 border-b border-slate-200">
            <span className="text-xs text-slate-600">Critical Cash Level</span>
            <strong className="text-xs text-slate-900">
              {formatCurrency(criticalLevel)}
            </strong>
          </div>
          
          <div className="flex justify-between items-center pb-2 border-b border-slate-200">
            <span className="text-xs text-slate-600">Reorder Point</span>
            <strong className="text-xs text-slate-900">
              {calculations?.reorderPoint} units
            </strong>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-600">Safety Stock</span>
            <strong className="text-xs text-slate-900">
              {calculations?.safetyStockUnits} units
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsDashboard;